import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "react-query";

import {
  // deleteVideoDetails, // Commented out in favor of privacy toggle
  updateAssetPrivacy,
  getAssetList,
  postVideoDetails as postVideoDetailsApi,
} from "Api/Pages/VideoListApi";

import {
  ASSET_TYPE_ENUM,
  IAssetDetails,
  UPLOAD_PURPOSE_ENUM,
} from "Components/Shared/UploadAsset/types";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { getVideoColumns } from "./constants";
import USE_QUERY_KEYS_CONSTANTS from "Api/useQueryKeyConstants";
import AssetLandingPage from "Components/Shared/AssetLandingPage";
import { AssetType } from "Components/Shared/AssetLandingPage/types";
import { PromotionalVideoDataType } from "./types";
import { INITIAL_PAGE } from "App/constants";
import { isAllowedTo } from "Utils/helper";
import { useAppSelector } from "App/hooks";
import { AssetTagsData } from "Interfaces/AssetTagsInterface";

const PromotionalVideos = () => {
  const navigate = useNavigate();
  const {
    userDetails: { permissions, permission_rules },
  } = useAppSelector((state) => state.loginReducer);

  const [isModalOpen, setModalVisibility] = useState(false);
  let location = useLocation();
  let state = location.state as {
    currentPage: number;
  };
  const [currentPage, setCurrentPage] = useState<number>(
    state?.currentPage || INITIAL_PAGE
  );

  // toggle video details upload modal
  const toggleVideoUploadModal = () => {
    setModalVisibility((isOpen) => !isOpen);
  };

  const { data: result, refetch: fetchPromotionalVideos } = useQuery(
    USE_QUERY_KEYS_CONSTANTS.promotionalVideosList,
    () =>
      getAssetList({
        currentPage: currentPage,
        per_page: 10,
        upload_purpose: UPLOAD_PURPOSE_ENUM.featured,
        of_type: ASSET_TYPE_ENUM.video,
      }),
    { refetchOnWindowFocus: false, enabled: false }
  );

  const onRowClick = (rowData: AssetType) => {
    // navigate to details page/modal
    navigate(`/featured-videos/${rowData.id}`, {
      state: { ...rowData, currentPage: currentPage },
    });
  };

  const onTogglePrivacyClicked = async (
    selectedId: number,
    isPrivate: boolean
  ) => {
    await updateAssetPrivacy(selectedId, isPrivate);
    // On successful update fetch featured videos
    fetchPromotionalVideos();
  };

  const canTogglePrivacy = (row: PromotionalVideoDataType) => {
    const allowed = isAllowedTo("Asset", "update", permissions);
    const updateRule = permission_rules?.update?.Asset as
      | { assetable_id?: number; assetable_type?: string }
      | undefined;
    if (!allowed) return false;
    if (!updateRule) return true;
    const matchesId =
      updateRule.assetable_id === undefined ||
      updateRule.assetable_id === (row as unknown as AssetType).assetable_id;
    const matchesType =
      !updateRule.assetable_type ||
      updateRule.assetable_type ===
        (row as unknown as AssetType).assetable_type;
    return matchesId && matchesType;
  };

  const onVideoSaveCallback = async (
    videoDetails: IAssetDetails,
    assetTagsData: AssetTagsData
  ) => {
    const { title, description, thumbnail_url, kpoint_gcc_id } = videoDetails;

    await postVideoDetailsApi({
      title,
      description,
      thumbnail_url,
      kpoint_gcc_id,
      of_type: ASSET_TYPE_ENUM.video,
      upload_purpose: UPLOAD_PURPOSE_ENUM.featured,
      ...assetTagsData,
    });

    fetchPromotionalVideos();
  };

  const handleVideoSubmissionNavigate = () => {
    navigate("/featured-videos/submissions");
  };

  useEffect(() => {
    //fetch featured videos from server
    fetchPromotionalVideos();
  }, [fetchPromotionalVideos, currentPage]);

  return (
    <AssetLandingPage
      uploadModalProps={{
        purpose: UPLOAD_PURPOSE_ENUM.featured,
      }}
      title="Featured Videos"
      subActionButtonProps={{
        label: "Video Submissions",
        onClickHandler: handleVideoSubmissionNavigate,
      }}
      actionButtonProps={{
        label: "Upload Video",
        icon: faPlus,
        onClickHandler: toggleVideoUploadModal,
      }}
      tableProps={{
        columns: getVideoColumns(onTogglePrivacyClicked, canTogglePrivacy),
        tableData: result?.data || [],
        noDataMessage: "Please upload featured videos",
        onRowClick,
        clickable: true,
        paginationProps: {
          totalCount: result?.total_count || 0,
          currentPage: currentPage,
          numberOfRowPerPage: 10,
          setPageNumber: setCurrentPage,
        },
      }}
      isUploadModalOpen={isModalOpen}
      //sizeConfig={getSizeConfig(assetConfig)}

      toggleVideoUploadModal={toggleVideoUploadModal}
      onVideoSaveCallback={onVideoSaveCallback}
    />
  );
};

export default PromotionalVideos;
